import axios from "axios";
// Reuses the main API's request-body redaction so there is ONE sensitive-key
// list to extend. This import closes a cycle -- api.js -> utils/utils.js ->
// apiAdmin.js -> api.js -- which is safe only because each side reaches the
// other at CALL time (utils' audit() calls sendLog; the interceptor below calls
// the helper), never while the modules evaluate. Keep it that way: do not call
// it while building the instance and do not hoist it into a module-level
// destructure, or whichever module loads first sees an uninitialised binding.
import { sanitizeApiErrorRequestBody } from "./api";

// The helper redacts a fixed list of KNOWN sensitive keys (email, dob, audio,
// ...), which fits the main API's structured payloads. This service is
// different: its bodies are prose under neutral key names -- the AI Support
// chat's `message`, a ticket's `description`, a voice `transcript`, a `log`
// line -- and any of them can carry patient details the clinician just typed.
// So after the redaction pass every remaining string value is reduced to its
// length. Shape and sizes keep the message useful ("was threadId null?", "how
// big was the payload?") while nothing a person typed can reach the error
// message -- and through it console.error, and through that Sentry.
const REDACTED_MARKER = "[REDACTED]";
const summarizeStrings = (value) => {
  if (Array.isArray(value)) return value.map(summarizeStrings);
  if (typeof value === "string") return value === REDACTED_MARKER ? value : `[text ${value.length} chars]`;
  if (!value || typeof value !== "object") return value;
  const safe = {};
  for (const [key, nested] of Object.entries(value)) safe[key] = summarizeStrings(nested);
  return safe;
};

const describeRequestBody = (rawBody) => JSON.stringify(summarizeStrings(JSON.parse(sanitizeApiErrorRequestBody(rawBody))));

// Type and size only. The response body of a failed admin call can echo the
// request (validation errors quote the offending field) or carry the
// upstream's own message, so its content is never put in the error message;
// callers that need it still have the full axios error on e.cause.
const describeResponseBody = (response) => {
  if (!response || typeof response !== "object") return typeof response;
  const data = response.data;
  if (data === undefined || data === null || data === "") return "empty";
  if (typeof data === "string") return `text, ${data.length} chars`;
  if (typeof data === "object") {
    try {
      return `json, ${JSON.stringify(data).length} chars`;
    } catch {
      return "object";
    }
  }
  return typeof data;
};

export const createApi = () => {
  const api = axios.create({
    baseURL: process.env.REACT_APP_ENDPOINT_ADMIN,
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "application/json",
    },
    timeout: 300000,
    withCredentials: true,
  });

  // Everything in e.message may end up in Sentry: several callers log it via
  // console.error on purpose (ModalAISupport keeps that window open because the
  // outage it caught was only ever visible there). So the message carries the
  // request's identity and a PHI-free summary of its shape -- never the typed
  // text and never the response body. The raw axios error stays on e.cause for
  // code that needs it; e.status is lifted out so callers need not dig.
  api.interceptors.response.use(null, (res) => {
    const method = typeof res?.config?.method === "string" ? res.config.method.toUpperCase() : undefined;
    const status = res?.response?.status;
    let error = `API Error\n${method} ${res?.config?.url}\nResponse: ${status ?? res?.status} - ${res?.code}`;
    if (res?.config?.data) {
      let body = "[unavailable]";
      try {
        body = describeRequestBody(res.config.data);
      } catch {
        // Not JSON (or the helper is unavailable under a test's module mock):
        // say so rather than fall back to the raw text.
      }
      error += `\nRequest body: ${body}`;
    }
    if (res?.response !== undefined && res?.response !== null) {
      error += `\nResponse body: ${describeResponseBody(res.response)}`;
    }
    const e = new Error(error);
    e.cause = res;
    e.status = status;
    throw e;
  });

  // Tickets
  const getTickets = () => {
    return api.get(`/api/tickets/lucas`);
  };

  const getTicket = (ticketId) => {
    return api.get(`/api/tickets/lucas/${ticketId}`);
  };

  const sendMessage = (ticketId, message) => {
    return api.post(`/api/tickets/lucas/${ticketId}/sendMessage`, { message });
  };

  const createTicket = (ticket) => {
    return api.post(`/api/tickets/lucas`, ticket);
  };

  // Audit
  const sendLog = (log, level, user, chatId, sessionId = "web", deviceId = "web") => {
    let org = localStorage.getItem("organization");

    return api.post(
      "/api/logs",
      {
        log,
        type: typeof log,
        level,
        sessionId,
        deviceId,
        user,
        chatId,
        org,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  };

  // Chat
  const sendChatMessage = (message, threadId, socketId) => {
    let brand = sessionStorage.getItem("brand");
    return api.post(`/api/sessions/sendMessage`, { message, threadId, socketId, brand });
  };

  const getOpenAIKey = () => {
    return api.get("/api/twilio/getLucasKey");
  };

  const getPublicOpenAIKey = (encounterId, secretToken, organizationId) => {
    return api.post(`/api/sessions/getKey`, { encounterId, secretToken, organizationId });
  };

  const submitVoiceEncounterTranscript = (encounterId, secretToken, organizationId, transcript) => {
    return api.post("/api/sessions/submitVoiceEncounterTranscript", { encounterId, secretToken, organizationId, transcript });
  };

  const initiatePhoneCall = (encounterId, secretToken, organizationId, phone, socketId) => {
    return api.post("/api/sessions/call", { encounterId, secretToken, organizationId, phone, socketId });
  };

  // Sessions
  const createSession = (session) => {
    return api.post("/api/sessions/create", session);
  };

  const listSessions = (organizationId, tab, limit) => {
    return api.post("/api/sessions/list", { organizationId, tab, limit });
  };

  const getSessionStats = (organizationId) => {
    return api.post("/api/sessions/stats", { organizationId });
  };

  const closeSession = (sessionId) => {
    return api.post("/api/sessions/close", { sessionId });
  };

  const listPatientSessions = (patientId) => {
    return api.post("/api/sessions/patient", { patientId });
  };

  // return functions for interface
  return {
    // Tickets
    getTickets,
    getTicket,
    sendMessage,
    createTicket,

    // Chat
    sendChatMessage,

    // Audit
    sendLog,

    // AI Agent
    getOpenAIKey,
    getPublicOpenAIKey,
    submitVoiceEncounterTranscript,
    initiatePhoneCall,

    // Sessions
    createSession,
    listSessions,
    getSessionStats,
    closeSession,
    listPatientSessions,

    // Utils
    api,
  };
};

// Creates a singleton
export default createApi();
